import { getHelpCenterUrl } from '@/helpers/getHelpCenterUrl';

import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import {
  DICTATION_GUIDE_PATH,
  getDictationModalQueuePorts,
  newInThisReleaseCardMessages,
} from '@heidi/dictate';
import { ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { NewInThisReleaseCard } from './new-in-this-release-card';

const MODAL_ID = ModalId.DICTATION_NEW_IN_RELEASE_CARD;

/**
 * Queue-managed launch surface for the dictation "New in this release" card.
 * Mirrors the post-upgrade welcome popup: a bottom-start floating card anchored
 * to a fixed point, shown once per user. Visibility is owned by the modal queue
 * via {@link useQueuedModal}; the launch condition lives in
 * `launchModalsConfigAtom`. "Ok got it" dismisses, "Learn more" opens the
 * dictation help article — both persist the seen state so it never reappears.
 */
export const NewInThisReleasePopup = () => {
  const intl = useIntl();
  const { isOpen, closeWithOptions, onOpenChange } =
    getDictationModalQueuePorts().useQueuedModal(MODAL_ID);

  const handleDismiss = useCallback(
    () => closeWithOptions({ action: ModalViewAction.dismiss }),
    [closeWithOptions]
  );

  const handleLearnMore = useCallback(() => {
    window.open(
      getHelpCenterUrl(DICTATION_GUIDE_PATH, intl.locale),
      '_blank',
      'noopener,noreferrer'
    );
    closeWithOptions({ action: ModalViewAction.engage });
  }, [closeWithOptions, intl.locale]);

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <PopoverAnchor
        aria-hidden
        className="pointer-events-none fixed bottom-4 start-[70px] z-[55] size-px"
      />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={0}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        aria-label={intl.formatMessage(newInThisReleaseCardMessages.ariaLabel)}
        data-testid="new-in-this-release-popup"
        className="w-auto border-0 bg-transparent p-0 shadow-none"
      >
        <NewInThisReleaseCard onLearnMore={handleLearnMore} onDismiss={handleDismiss} />
      </PopoverContent>
    </Popover>
  );
};
